import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import {
  buildPreviewDocument,
  buildStaticPreviewDocument,
  clampDimension,
  downloadBlob,
  formatSize,
  outputFilename,
  renderHtmlToImage,
  type HtmlOutputFormat,
  type HtmlRenderResult,
} from '../model/htmlRendering'

const UTILITY_KEY = 'html-to-image'

const EXAMPLE_HTML = `<section style="width:100%;min-height:100%;padding:40px;background:linear-gradient(135deg,#f8fafc,#e0f2fe);font-family:Arial,sans-serif;">
  <div style="max-width:760px;margin:0 auto;padding:28px;border-radius:16px;background:white;box-shadow:0 18px 45px rgba(15,23,42,.14);">
    <p style="margin:0 0 10px;color:#0f766e;font-size:14px;font-weight:700;text-transform:uppercase;">imgify preview</p>
    <h1 style="margin:0;color:#0f172a;font-size:42px;line-height:1.05;">HTML в картинку</h1>
    <p style="margin:18px 0 0;color:#475569;font-size:18px;line-height:1.55;">Вставьте HTML, выберите размер и скачайте PNG или JPG.</p>
  </div>
</section>`

export function HtmlToImageTool() {
  const t = useT()
  const copy = t.htmlToImage
  const [html, setHtml] = useState(EXAMPLE_HTML)
  const [width, setWidth] = useState(1200)
  const [height, setHeight] = useState(630)
  const [format, setFormat] = useState<HtmlOutputFormat>('png')
  const [result, setResult] = useState<HtmlRenderResult | null>(null)
  const [previewDocument, setPreviewDocument] = useState(() => buildStaticPreviewDocument(EXAMPLE_HTML))
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    return () => {
      if (result) {
        URL.revokeObjectURL(result.url)
      }
    }
  }, [result])

  useEffect(() => {
    setPreviewDocument(buildPreviewDocument(html))
  }, [html])
  const canRender = html.trim().length > 0 && !isProcessing

  const clearResult = useCallback(() => {
    setResult((current) => {
      if (current) {
        URL.revokeObjectURL(current.url)
      }
      return null
    })
  }, [])

  const updateHtml = useCallback(
    (value: string) => {
      setHtml(value)
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const updateWidth = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setWidth(clampDimension(Number(event.target.value)))
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const updateHeight = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setHeight(clampDimension(Number(event.target.value)))
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const updateFormat = useCallback(
    (nextFormat: HtmlOutputFormat) => {
      setFormat(nextFormat)
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''

      if (!file) return

      if (!file.name.toLowerCase().endsWith('.html') && file.type !== 'text/html') {
        setError(copy.errorsText.unsupportedFile)
        toolError(UTILITY_KEY, 'unsupported_file')
        return
      }

      try {
        const text = await file.text()
        updateHtml(text)
      } catch {
        setError(copy.errorsText.readFailed)
        toolError(UTILITY_KEY, 'read_failed')
      }
    },
    [copy.errorsText.readFailed, copy.errorsText.unsupportedFile, updateHtml],
  )

  const handleRender = useCallback(async () => {
    const startedAt = performance.now()
    toolInvoke(UTILITY_KEY, {
      input: 'html',
      format,
      width,
      height,
      processing: 'local',
    })
    setIsProcessing(true)
    setError(null)
    clearResult()

    try {
      const nextResult = await renderHtmlToImage(html, width, height, format, copy.errorsText)
      setResult(nextResult)
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startedAt), {
        input: 'html',
        format,
        width,
        height,
        processing: 'local',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : copy.errorsText.exportFailed
      setError(message)
      toolError(UTILITY_KEY, 'render_failed', {
        format,
        width,
        height,
        processing: 'local',
      })
    } finally {
      setIsProcessing(false)
    }
  }, [clearResult, copy.errorsText, format, height, html, width])

  const handleDownload = useCallback(() => {
    if (!result) return
    downloadBlob(result.blob, outputFilename(result.format))
  }, [result])

  return (
    <div className="mx-auto max-w-6xl">
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.02fr)_minmax(360px,0.98fr)] lg:items-start">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2 sm:mb-4">
            <LocalizedProcessingBadge variant="local" />
            <span className="rounded-md border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground">
              HTML -&gt; PNG/JPG
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
            {copy.title}
          </h1>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-4 sm:text-base sm:leading-7">
            {copy.description}
          </p>
        </div>

        <Card className="hidden rounded-lg shadow-sm lg:block">
          <CardHeader>
            <CardTitle>{copy.summaryTitle}</CardTitle>
            <CardDescription>{copy.privacyNote}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm">
            <SummaryRow label={copy.inputTypeLabel} value={copy.inputTypeValue} />
            <SummaryRow label={copy.processingLabel} value={copy.processingValue} />
            <SummaryRow label={copy.outputLabel} value={`${width}x${height} ${format.toUpperCase()}`} />
            {result ? (
              <SummaryRow label={copy.resultSizeLabel} value={formatSize(result.blob.size)} />
            ) : null}
          </CardContent>
        </Card>
      </section>

      <section className="mt-3 grid grid-cols-1 gap-4 sm:mt-7 lg:grid-cols-[minmax(0,1fr)_420px]">
        <Card className="rounded-lg shadow-sm">
          <CardHeader>
            <CardTitle>{copy.editorTitle}</CardTitle>
            <CardDescription>{copy.editorDescription}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
                {copy.uploadHtml}
              </Button>
              <Button type="button" variant="ghost" onClick={() => updateHtml(EXAMPLE_HTML)}>
                {copy.resetExample}
              </Button>
              <input
                ref={inputRef}
                className="hidden"
                type="file"
                accept=".html,text/html"
                onChange={handleFileChange}
              />
            </div>

            <textarea
              value={html}
              onChange={(event) => updateHtml(event.target.value)}
              spellCheck={false}
              className="min-h-[360px] w-full resize-y rounded-md border bg-background p-3 font-mono text-sm leading-6 text-foreground shadow-inner outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={copy.htmlLabel}
            />

            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="rounded-lg shadow-sm">
          <CardHeader>
            <CardTitle>{copy.settingsTitle}</CardTitle>
            <CardDescription>{copy.settingsDescription}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1 text-sm font-medium">
                {copy.widthLabel}
                <input
                  type="number"
                  min={1}
                  max={4000}
                  value={width}
                  onChange={updateWidth}
                  className="h-10 rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                {copy.heightLabel}
                <input
                  type="number"
                  min={1}
                  max={4000}
                  value={height}
                  onChange={updateHeight}
                  className="h-10 rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium">{copy.formatLabel}</div>
              <div className="grid grid-cols-2 rounded-md border bg-muted/40 p-1">
                {(['png', 'jpg'] as const).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => updateFormat(item)}
                    className={cn(
                      'h-9 rounded-sm text-sm font-medium transition-colors',
                      format === item
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {item.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-md border bg-background">
              <iframe
                title={copy.previewTitle}
                srcDoc={previewDocument}
                sandbox=""
                className="h-[280px] w-full bg-white"
              />
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Button type="button" onClick={handleRender} disabled={!canRender}>
                {isProcessing ? copy.rendering : copy.renderButton}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleDownload}
                disabled={!result}
              >
                {copy.downloadButton}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        {copy.faq.map((item) => (
          <Card key={item.question} className="rounded-lg shadow-sm">
            <CardHeader>
              <CardTitle className="text-base leading-6">{item.question}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm leading-6 text-muted-foreground">
              {item.answer}
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  )
}

